import { Button, Col, Row } from 'antd';
import { FC } from 'react';
import {
  amountTooHighIcon,
  checkSquareIcon,
  fileProhibitedIndustry,
  fileSanctionedTerms,
  financingIcon,
  frrIcon,
  guestIcon,
  mail,
  orgOwesMoney,
  paperClip,
  prohibitedIndustry,
  prohibitedIndustryInvoice,
  receiverIcon,
  rtpIcon,
  sanctionedTerms,
  sanctionedTermsError,
  sanctionsIcon,
  shipmentInTransit,
  testBoard
} from '@tools/ui/assets';
import { PayrollIndicator } from '@tools/ui/components/common/PayrollIndicator';
import { IconSize, InsightLabel, InsightType } from '@tools/ui/dime';
import {
  Bill,
  EnumTaskEntityCachedDataPaymentDeliveryPreference,
  Maybe,
  PaymentdeliveryPreferenceEnumType,
  RecurringBill,
  SherlockDecisionTaggingEntityCachedData,
  TaskEntityCachedData,
  TaskEntityCachedDataFeatures
} from '@tools/ui/types/generated/graphql';
import useT from '@tools/ui-hooks/useT';
import { theme } from '@tools/ui-utils/theme.util';
import inboxDrawerStore, {
  DetailsTabRubrics
} from '../../stores/inboxDrawerStore';
import IconWrap from '../common/icons/antIconWrap';
import MelioMeIcon from '../common/icons/melioMe.icon';
import TableIcon from '../common/icons/tableIcon.icon';
import RecurringBillIcon from './recurringBillIcon';

export type DataValue = string | number | boolean | null | undefined;

export type IconProperties = {
  icon?: React.FunctionComponent<
    React.PropsWithChildren<React.SVGProps<SVGSVGElement> & { title?: string }>
  >;
  customComponent?: JSX.Element;
  tooltip?: string;
  renderTooltip?(value: DataValue): string;
  possibleValuesToHide?: DataValue[];
  possibleValuesToShow?: DataValue[];
  color?:
    | {
        true?: string;
        false?: string;
        [key: number]: string;
      }
    | string;
  renderColor?(value: DataValue): string;
  display?: {
    drawer?: boolean;
    table?: boolean;
  };
  style?: React.CSSProperties | undefined;
  onIconClick?: () => void;
};

export type IProps = {
  cachedData?:
    | TaskEntityCachedData
    | SherlockDecisionTaggingEntityCachedData
    | { bill?: Maybe<Bill>; recurringBill?: Maybe<RecurringBill> };
  attributes?:
    | Record<string, DataValue>
    | TaskEntityCachedDataFeatures
    | SherlockDecisionTaggingEntityCachedData
    | null;
  collection: string;
  location?: string;
  deliveryPreference?: Maybe<
    | PaymentdeliveryPreferenceEnumType
    | EnumTaskEntityCachedDataPaymentDeliveryPreference
  >;
};

export const featuresExtraFields = [
  // payment features
  'features.vendorHasHandle',
  'features.isPaymentExpedited',
  'features.isGoodsReceived',
  'features.invoiceOrNoteHasSanctionedWord',
  'features.invoiceOrNoteHasSanctionedWordError',
  'features.hasFileRequests',

  // org features
  'features.doesOrgOweMoney',
  'features.isEmailVerified',
  'features.numManuallyApprovedPayments',
  'features.isFRR',
  'features.payorIdmScreeningNumHits',
  'features.payorCreateOrigin',

  // vendor features
  'features.numOrganizationVendorCompletedPayments',
  'features.payeeIdmScreeningNumHits',

  // fs features
  'features.isHighAmountForFS',
  'features.isCardWithSimilarDataApproved'
];

const AttributeIcon: FC<React.PropsWithChildren<IProps>> = ({
  attributes,
  collection,
  location = '',
  cachedData,
  deliveryPreference
}) => {
  const { t } = useT();
  const { setActiveRubricId } = inboxDrawerStore;

  const iconPropertiesByCollection: {
    [collection: string]: { [attributeName: string]: IconProperties };
  } = {
    payment: {
      isGoodsReceived: {
        icon: receiverIcon,
        renderTooltip: (isGoodsReceived: boolean | null): string => {
          return t('features.tooltip.isGoodsReceived', {
            isGoodsReceived: isGoodsReceived === false ? 'not ' : ''
          });
        },
        color: {
          true: theme.colors.boolean.yes,
          false: theme.colors.boolean.no
        }
      },
      isPaymentExpedited: {
        icon:
          deliveryPreference === PaymentdeliveryPreferenceEnumType.rtp ||
          deliveryPreference ===
            EnumTaskEntityCachedDataPaymentDeliveryPreference.rtp
            ? rtpIcon
            : shipmentInTransit,
        tooltip:
          deliveryPreference === PaymentdeliveryPreferenceEnumType.rtp ||
          deliveryPreference ===
            EnumTaskEntityCachedDataPaymentDeliveryPreference.rtp
            ? t('features.tooltip.rtpPayment')
            : t('features.tooltip.expeditedPayment'),
        possibleValuesToShow: [true]
      },
      invoiceOrNoteHasSanctionedWord: {
        icon: sanctionedTerms,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.invoiceOrNoteHasSanctionedWord'),
        onIconClick: () => {
          setActiveRubricId(DetailsTabRubrics.payment);
        }
      },
      invoiceOrNoteHasSanctionedWordError: {
        icon: sanctionedTermsError,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.invoiceOrNoteHasSanctionedWordError'),
        onIconClick: () => {
          setActiveRubricId(DetailsTabRubrics.payment);
        }
      },
      isTest: {
        icon: () => (
          <IconWrap component={testBoard} style={{ color: 'black' }} />
        ),
        tooltip: t('payment.testPaymentIconTooltip'),
        possibleValuesToShow: [true]
      },
      isSuspectedOfProhibitedIndustries: {
        icon: prohibitedIndustryInvoice,
        possibleValuesToShow: [true],
        tooltip: t('payment.prohibitedIndustryIconTooltip')
      },
      hasFileRequests: {
        icon: paperClip,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.fileAttached'),
        onIconClick: () => {
          setActiveRubricId(DetailsTabRubrics.payment);
        }
      },
      isPayroll: {
        possibleValuesToShow: [true],
        customComponent: <PayrollIndicator size={IconSize.Small} />
      }
    },
    organization: {
      doesOrgOweMoney: {
        icon: orgOwesMoney,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.orgOwesMoney'),
        color: {
          true: theme.colors.boolean.no
        }
      },
      orgOwesUsMoney: {
        icon: orgOwesMoney,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.orgOwesMoney'),
        color: {
          true: theme.colors.boolean.no
        }
      },
      isEmailVerified: {
        icon: mail,
        possibleValuesToShow: [false],
        tooltip: t('features.tooltip.emailVerified'),
        color: {
          false: theme.colors.boolean.no
        }
      },
      numManuallyApprovedPayments: {
        icon: frrIcon,
        possibleValuesToShow: [0],
        tooltip: t('features.tooltip.frr')
      },
      isFRR: {
        icon: frrIcon,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.frr')
      },
      payorIdmScreeningNumHits: {
        icon: sanctionsIcon,
        possibleValuesToHide: [0, null],
        tooltip: `${t('features.tooltip.payorNumHits')}`
      },
      payorCreateOrigin: {
        icon: guestIcon,
        tooltip: t('features.tooltip.payorCreateOriginIsGuest'),
        possibleValuesToShow: ['guest']
      },
      isSuspectedOfProhibitedIndustries: {
        icon: prohibitedIndustry,
        possibleValuesToShow: [true],
        tooltip: t('payment.prohibitedIndustryIconTooltip')
      }
    },
    vendor: {
      numOrganizationVendorCompletedPayments: {
        icon: checkSquareIcon,
        possibleValuesToHide: [0, null],
        renderTooltip: (value: number | null): string => {
          return t('features.tooltip.numOrganizationVendorCompletedPayments', {
            value
          });
        },
        color: theme.colors.boolean.yes
      },
      vendorHasHandle: {
        icon: MelioMeIcon,
        possibleValuesToShow: [true],
        tooltip: ''
      },
      payeeIdmScreeningNumHits: {
        icon: sanctionsIcon,
        possibleValuesToHide: [0, null],
        tooltip: `${t('features.tooltip.payeeNumHits')}`
      },
      isSuspectedOfProhibitedIndustries: {
        icon: prohibitedIndustry,
        possibleValuesToShow: [true],
        tooltip: t('payment.prohibitedIndustryIconTooltip')
      }
    },
    fs: {
      isHighAmountForFS: {
        icon: amountTooHighIcon,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.highAmountFs')
      },
      isCardWithSimilarDataApproved: {
        icon: checkSquareIcon,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.isCardWithSimilarDataApproved'),
        color: theme.colors.boolean.yes
      },
      isApFinancingPayment: {
        icon: financingIcon,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.isApFinancingPayment'),
        style: {
          marginTop: 2
        }
      }
    },
    deliveryMethod: {
      isOriginalDmEqualToCurrentDM: {
        possibleValuesToShow: [false],
        customComponent: <InsightLabel type={InsightType.CHANGED} />
      }
    },
    file: {
      isSuspectedOfProhibitedIndustries: {
        icon: fileProhibitedIndustry,
        possibleValuesToShow: [true],
        tooltip: t('payment.prohibitedIndustryIconTooltip')
      },
      invoiceOrNoteHasSanctionedWord: {
        icon: fileSanctionedTerms,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.fileInvoiceOrNoteHasSanctionedWord')
      },
      isOcrError: {
        icon: sanctionedTermsError,
        possibleValuesToShow: [true],
        tooltip: t('features.tooltip.fileInvoiceOrNoteHasSanctionedWordError')
      }
    }
  };

  return (
    <Row gutter={theme.spacing(0.5)} align="middle" wrap={false}>
      {Object.keys(attributes ?? {}).map((attributeName: string) => {
        const value = attributes?.[attributeName];
        const iconProperties = iconPropertiesByCollection?.[collection]?.[
          attributeName
        ] as IconProperties;
        if (
          iconProperties &&
          value !== null &&
          (iconProperties.possibleValuesToShow?.includes(value) ||
            iconProperties.possibleValuesToShow === undefined) &&
          (!iconProperties.possibleValuesToHide?.includes(value) ||
            iconProperties.possibleValuesToHide === undefined) &&
          (iconProperties.display?.[location] === undefined ||
            iconProperties.display?.[location])
        ) {
          const tableIcon = (
            <TableIcon
              tooltip={
                iconProperties.renderTooltip?.(value) || iconProperties.tooltip
              }
              component={iconProperties.icon}
              style={{
                color:
                  typeof iconProperties?.color === 'string'
                    ? iconProperties.color
                    : iconProperties?.color?.[`${value}`],
                marginRight: 10,
                ...iconProperties.style
              }}
            />
          );

          const buttonTableIcon = iconProperties.onIconClick && (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={iconProperties.onIconClick}
            >
              {tableIcon}
            </Button>
          );
          return (
            <Col key={attributeName}>
              {iconProperties?.customComponent || buttonTableIcon || tableIcon}
            </Col>
          );
        }
        return null;
      })}
      {location === 'table' &&
      cachedData?.recurringBill &&
      cachedData.bill?.recurringBillIndex ? (
        <RecurringBillIcon
          recurringBillIndex={cachedData.bill.recurringBillIndex}
          occurrences={cachedData?.recurringBill.occurrences as number}
        />
      ) : (
        <></>
      )}
    </Row>
  );
};

export default AttributeIcon;
